/**
 * リアルタイムテスト API
 *
 * AppSync Events 経由でテスト問題の配信・回答送信を行う。
 * チャット (realtimeChat.ts) と同じ AppSync Events パターンだが、
 * 別の AppSync EventApi（Question Backend CDK）に接続する。
 */

import { ensureFrontendSettings, getCachedFrontendSettings } from './frontendSettings';

import type { Question, Choice } from '../types/test';
import { updateServerTimeOffset } from '../utils/serverTime';

// =============================================================================
// 型定義
// =============================================================================

/** AppSync Events 接続設定（テスト用） */
export interface QuestionEventsConfig {
  realtimeEndpoint: string;
  httpEndpoint: string;
  apiKey: string;
  restApiUrl: string;
}

/**
 * バックエンドから受信するイベントペイロード
 *
 * onPublish Lambda の処理結果により、イベントタイプごとに構造が異なる:
 * - QuestionOpened / QuestionClosed: フラット（onPublish で加工済み）
 *   → { eventType, tenant_id, question_id, display_number, question, ... }
 * - AnswerReceived: フラット + camelCase
 *   → { eventType, questionId, userId, status }
 * - ExplanationEnded / TestCancelled: ネスト（onPublish でパススルー）
 *   → { eventType, payload: { tenant_id, question_id, ... } }
 * 
 * 注: question_num / questionNum は後方互換性のために残しているが、推奨は question_id
 */
export interface QuestionEventPayload {
  eventType:
    | 'QuestionOpened'
    | 'QuestionClosed'
    | 'ExplanationEnded'
    | 'TestCancelled'
    | 'AnswerReceived'
    | 'AnswerRejected'
    | 'StatusChanged';
  /** ネスト構造のイベント（ExplanationEnded / TestCancelled 等） */
  payload?: {
    tenant_id?: string;
    lecture_id?: string;
    question_id?: number; // 不変の問題ID
    question_num?: number; // 表示用問題番号（後方互換性のため残す）
    display_number?: number | null;
    status?: string;
    [key: string]: unknown;
  };
  /** フラット構造のフィールド（QuestionOpened / QuestionClosed 等） */
  tenant_id?: string;
  lecture_id?: string;
  question_id?: number; // 不変の問題ID（snake_case）
  question_num?: number; // 後方互換性のため残す（非推奨）
  questionId?: number; // 不変の問題ID（camelCase、AnswerReceived用）
  questionNum?: number; // 後方互換性のため残す（非推奨）
  display_number?: number | null;
  status?: string;
  question?: BackendQuestion;
  correct_choice?: number;
  total_answers?: number;
  correct_count?: number;
  ranking?: RankingEntry[];
  scheduled_close_time?: number;
  timestamp?: number;
  reason?: string;
  userId?: string;
  [key: string]: unknown;
}

/**
 * バックエンドの問題フォーマット
 * CDK は QuestionOpened では question_id / question_text / time_limit を送信し、
 * QuestionClosed では question_id / correct_choice / explanation / choices を送信する。
 * /questions/ended は questiontext フィールドを使用（APIによって異なる）
 */
export interface BackendQuestion {
  id?: number;
  question_id?: number;
  internal_id?: string;
  question_text?: string;
  questiontext?: string;  // /questions/ended API用
  question_images?: Array<{ file_url?: string; image_url?: string; label?: string }> | string[] | null;
  choices: BackendChoice[];
  time_limit?: number;
  limittime?: number;  // /questions/ended API用
  level?: number;
  format_type?: string;
  help_text?: string | null;
  chart_style?: number;
  sort_key?: number;
  question_type?: string;
  status?: string;
  correct_choice?: number;
  explanation?: string;
}

interface BackendChoice {
  id?: number;
  num?: number;
  internal_id?: string;
  choice?: string;
  text?: string;
  sort_key?: number;
  has_additional_question?: boolean;
  additional_question?: string | null;
  is_correct?: boolean;
  description?: string | null;
}

export interface RankingEntry {
  rank: number;
  user_id: string;
  answer_time_ms: number;
  answer_time_sec: string;
}

/** 回答送信データ */
export interface SubmitAnswerData {
  lecture_id: string;
  question_id: number; // 不変の問題ID
  choice_num: number;
  choice_internal_id: string; // 選択肢の一意識別子（必須）
  user_id: string;
  answer_time_ms: number;
}

// =============================================================================
// 設定取得
// =============================================================================

/**
 * テスト設定を取得（frontend-settings APIのキャッシュを参照）
 *
 * 呼び出し前に ensureFrontendSettings() で設定を取得しておくこと。
 */
export function getQuestionConfig(): QuestionEventsConfig {
  const settings = getCachedFrontendSettings();
  const realtimeEndpoint = settings?.questionRealtimeEndpoint || '';
  const httpEndpoint = settings?.questionHttpEndpoint || '';
  const apiKey = settings?.questionApiKey || '';
  const restApiUrl = settings?.questionRestApiUrl || '';

  if (!realtimeEndpoint || !httpEndpoint || !apiKey) {
    console.warn('[Question] テスト設定が不完全です。管理画面のAppSync設定を確認してください。');
  }

  return { realtimeEndpoint, httpEndpoint, apiKey, restApiUrl };
}

// =============================================================================
// API 関数
// =============================================================================

/**
 * onPublish Lambda が回答を業務判定で拒否した（締切済み・形式不正など）ことを表すエラー。
 *
 * publish の HTTP 2xx は「AppSync Events がイベントを受け付けた」ことしか意味せず、
 * onPublish Lambda の締切・形式チェックの結果ではない（受講生B-5）。
 * Lambda が拒否したイベントはレスポンスボディの failed 配列に入るため、
 * それを検知した場合にこのエラーを投げる。
 */
export class AnswerRejectedError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AnswerRejectedError';
  }
}

/**
 * AppSync Events HTTP 経由で回答を送信する。
 *
 * HTTP 2xx でも onPublish Lambda が拒否したイベントは failed に入るため、
 * その場合は AnswerRejectedError を投げる（受講生B-5）。
 */
export async function publishAnswer(
  channel: string,
  data: SubmitAnswerData,
  options?: { signal?: AbortSignal },
): Promise<void> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  const url = `https://${config.httpEndpoint}/event`;

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: config.apiKey,
    },
    body: JSON.stringify({
      channel,
      events: [JSON.stringify(data)],
    }),
    // ページ遷移・タブクローズ中でも送信を完了させる
    keepalive: true,
    // 締切到達時に進行中の送信を中断できるようにする
    signal: options?.signal ?? null,
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('[Question] 回答送信エラー:', response.status, errorText);
    throw new Error(`解答の送信に失敗しました: ${response.status}`);
  }

  // publish 2xx ≠ 業務受理。onPublish Lambda が拒否したイベントは failed に入る
  let result: {
    successful?: Array<{ identifier?: string; index?: number }>;
    failed?: Array<{ identifier?: string; index?: number; code?: number; errorMessage?: string; message?: string }>;
  } | null = null;
  try {
    result = await response.json();
  } catch {
    // ボディなし・JSON以外は従来どおり成功として扱う
  }
  if (result?.failed && result.failed.length > 0) {
    const reason = result.failed[0]?.errorMessage ?? result.failed[0]?.message ?? '';
    console.warn('[Question] 回答がサーバーで受理されませんでした:', result.failed);
    throw new AnswerRejectedError(reason);
  }
}

/**
 * 現在進行中の問題を REST API で取得する（途中参加・再接続用）
 *
 * GET /questions/current?tenant_id=xxx&lecture_id=xxx
 *
 * 出題中または集計済みの問題がある場合はそのデータを返す。
 * なければ null を返す（404 の場合）。
 */
export interface CurrentQuestionResponse {
  tenant_id: string;
  lecture_id: string;
  /** サーバー時刻（epoch ms）。時刻オフセット補正（S1）用。旧APIには存在しない */
  server_time?: number;
  current_question: {
    question_id: number; // 不変の問題ID
    display_number: number | null;
    status: string;
    question: BackendQuestion;
    aggregation: {
      total_answers: number;
      correct_count: number;
      choice_counts: Record<string, number>;
      avg_answer_time_ms: number;
      opened_at: number | null;
      closed_at: number | null;
      scheduled_close_time: number | null;
    };
  };
}

/**
 * 指定した問題が現在も回答受付中かどうかをサーバーに確認する。
 *
 * 回答送信リトライの再開前チェック用。クライアントが保持する締切予定時刻に依存せず、
 * サーバー側の最新状態（手動締切・出題切替を含む）で判定できる。
 *
 * @returns true: 回答受付中 / false: 締切済み（進行中の問題なし・別問題に切替済みを含む） /
 *          null: 判定不能（ネットワークエラー・APIエラー等。呼び出し側で受付中として扱う想定）
 */
export async function checkQuestionAnswerable(
  tenantId: string,
  lectureId: string,
  questionId: number,
): Promise<boolean | null> {
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    return null;
  }

  const url = `${config.restApiUrl}/questions/current?tenant_id=${encodeURIComponent(tenantId)}&lecture_id=${encodeURIComponent(lectureId)}&include_ended=true`;

  try {
    const requestStartMs = Date.now();
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    // 404 = 進行中の問題なし → 締切済みとみなす（確定的な判定）
    if (response.status === 404) {
      await sampleServerTimeFromBody(response, requestStartMs);
      return false;
    }

    if (!response.ok) {
      return null;
    }

    const data: CurrentQuestionResponse = await response.json();
    if (data?.server_time) {
      updateServerTimeOffset(data.server_time, requestStartMs, Date.now());
    }
    if (!data?.current_question) {
      return false;
    }
    // 別の問題に切り替わっている場合も、対象問題は締切済みとみなす
    if (data.current_question.question_id !== questionId) {
      return false;
    }
    return mapBackendStatus(data.current_question.status) === 'answering';
  } catch (error) {
    console.warn('[Question] 回答受付状態の確認に失敗:', error);
    return null;
  }
}

export async function fetchCurrentQuestion(
  tenantId: string,
  lectureId: string,
): Promise<CurrentQuestionResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    return null;
  }

  // include_ended=true を追加して、explanation_ended ステータスの問題も取得する
  const url = `${config.restApiUrl}/questions/current?tenant_id=${encodeURIComponent(tenantId)}&lecture_id=${encodeURIComponent(lectureId)}&include_ended=true`;

  try {
    const requestStartMs = Date.now();
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (response.status === 404) {
      await sampleServerTimeFromBody(response, requestStartMs);
      return null;
    }

    if (!response.ok) {
      console.warn('[Question] 現在の問題取得エラー:', response.status);
      return null;
    }

    const data: CurrentQuestionResponse = await response.json();
    if (data?.server_time) {
      updateServerTimeOffset(data.server_time, requestStartMs, Date.now());
    }
    return data;
  } catch (error) {
    console.warn('[Question] 現在の問題取得に失敗:', error);
    return null;
  }
}

/**
 * 404 等のエラーレスポンスのボディに含まれる server_time から
 * 時刻オフセットを計測する（S1対応）。ボディがない・パースできない場合は何もしない。
 */
async function sampleServerTimeFromBody(response: Response, requestStartMs: number): Promise<void> {
  try {
    const body = (await response.json()) as { server_time?: number } | null;
    if (body?.server_time) {
      updateServerTimeOffset(body.server_time, requestStartMs, Date.now());
    }
  } catch {
    // ボディなし・JSON以外は無視
  }
}

/**
 * 講義終了後の全問題データを取得する（途中参加・講義終了後の補完用）
 *
 * GET /questions/ended?tenant_id=xxx&lecture_id=xxx
 *
 * 講義ステータスが「ended」の場合のみデータを返す。
 * 講義中は403 Forbiddenが返される。
 */
export interface EndedQuestionsResponse {
  tenant_id: string;
  lecture_id: string;
  questions: Array<{
    display_number: number | null;
    status: string;
    question: BackendQuestion;
  }>;
}

export async function fetchEndedQuestions(
  tenantId: string,
  lectureId: string,
): Promise<EndedQuestionsResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    console.warn('[Question] REST API URLが設定されていません');
    return null;
  }

  const url = `${config.restApiUrl}/questions/ended?tenant_id=${encodeURIComponent(tenantId)}&lecture_id=${encodeURIComponent(lectureId)}`;

  try {
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (response.status === 403) {
      console.warn('[Question] 講義終了前のため /questions/ended にアクセスできません');
      return null;
    }

    if (response.status === 404) {
      console.warn('[Question] 問題が見つかりません');
      return null;
    }

    if (!response.ok) {
      console.warn('[Question] /questions/ended 取得エラー:', response.status);
      return null;
    }

    return await response.json();
  } catch (error) {
    console.warn('[Question] /questions/ended 取得に失敗:', error);
    return null;
  }
}

/**
 * 問題ごとの集計データを CDK REST API から取得する。
 *
 * GET /aggregation?tenant_id=xxx&lecture_id=xxx&question_id=xxx
 */
export interface AggregationResponse {
  tenant_id: string;
  lecture_id: string;
  question_id: number; // 不変の問題ID
  status: string;
  total_answers: number;
  correct_count: number;
  choice_counts: Record<string, number>;
  avg_answer_time_ms: number;
}

export async function fetchAggregation(
  tenantId: string,
  lectureId: string,
  questionId: number,
): Promise<AggregationResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) return null;

  const url = `${config.restApiUrl}/aggregation?tenant_id=${encodeURIComponent(tenantId)}&lecture_id=${lectureId}&question_id=${questionId}`;

  try {
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });
    if (!response.ok) return null;
    return await response.json();
  } catch (error) {
    console.warn(`[Question] 集計データ取得失敗 (question_id=${questionId}):`, error);
    return null;
  }
}

/**
 * 講義の全問題の集計データを一括取得する（受講生向け、認証不要）
 *
 * GET /aggregations?tenant_id=xxx&lecture_id=xxx
 */
export interface AggregationsResponse {
  tenant_id: string;
  lecture_id: string;
  aggregations: Array<{
    question_id: number;
    status: string;
    total_users: number;
    total_answers: number;
    correct_count: number;
    unanswered_count: number;
    choice_counts: Record<string, number>;
    choice_avg_times: Record<string, number>;
    avg_answer_time_ms: number;
    min_answer_time_ms: number;
    max_answer_time_ms: number;
    opened_at: number | null;
    closed_at: number | null;
    scheduled_close_time: number | null;
    updated_at: number | null;
  }>;
}

export async function fetchAggregations(
  tenantId: string,
  lectureId: string,
): Promise<AggregationsResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    console.warn('[Question] REST API URLが設定されていません');
    return null;
  }

  const url = `${config.restApiUrl}/aggregations?tenant_id=${encodeURIComponent(tenantId)}&lecture_id=${encodeURIComponent(lectureId)}`;

  try {
    console.log('[Question] 全問題の集計データを取得:', { tenantId, lectureId, url });
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (response.status === 404) {
      console.log('[Question] 集計データが見つかりません（テスト未開始またはデータなし）');
      return null;
    }

    if (!response.ok) {
      console.warn('[Question] 集計データ取得エラー:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[Question] 全問題の集計データ取得成功:', data.aggregations.length, '問');
    return data;
  } catch (error) {
    console.warn('[Question] 全問題の集計データ取得に失敗:', error);
    return null;
  }
}

/**
 * ユーザーの回答履歴を CDK REST API から取得する。
 *
 * GET /answers/history?tenant_id=xxx&user_id=xxx&lecture_id=xxx
 *
 * 講義終了後、ローカルストレージに回答データがない場合（別ブラウザなど）に使用。
 */
export interface AnswerHistoryResponse {
  user_id: string;
  lecture_id: number | string;
  tenant_id: string;
  answers: Array<{
    question_id: number; // 不変の問題ID
    choice_num: number;
    correct_choice: number;
    is_correct: boolean;
    answer_time_ms: number;
    answered_at: string;
  }>;
  summary?: {
    total_answers: number;
    correct_count: number;
    unanswered_count?: number;
    /** 正答率（%）。分母は実回答＋未回答（AGG-2対応でバックエンドと定義統一） */
    accuracy_rate: number;
  };
}

/**
 * ユーザーのランキング情報を取得する。
 *
 * GET /user-ranking?tenant_id=xxx&user_id=xxx&lecture_id=xxx
 *
 * 講義終了後、受講生個人のランキング情報（上位何%か）を取得する。
 */
export interface UserRankingResponse {
  user_id: string;
  lecture_id: string;
  tenant_id: string;
  ranking: {
    rank: number;
    total_users: number;
    percentile: number;
    tier: 20 | 40 | null;
  };
  user_stats: {
    total_answers: number;
    correct_count: number;
    unanswered_count?: number;
    /** 正答率（%）。分母は実回答＋未回答（AGG-2対応でバックエンドと定義統一） */
    accuracy_rate: number;
    avg_answer_time_ms: number;
  };
}

/**
 * ユーザーの回答履歴を取得する。
 *
 * @returns 回答履歴。404（未回答・データなし）の場合は answers が空配列のレスポンスを返す。
 *          API エラー・ネットワークエラー等で「回答の有無を判定できない」場合のみ null を返す
 *          （呼び出し側が「回答なし」と「判定不能」を区別できるようにするため。受講生B-3）。
 */
export async function fetchAnswerHistory(
  tenantId: string,
  userId: string,
  lectureId: string,
): Promise<AnswerHistoryResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    console.warn('[Question] REST API URLが設定されていません');
    return null;
  }

  const url = `${config.restApiUrl}/answers/history?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}&lecture_id=${encodeURIComponent(lectureId)}`;

  try {
    console.log('[Question] 回答履歴を取得:', { tenantId, userId, lectureId, url });
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (response.status === 404) {
      console.log('[Question] 回答履歴が見つかりません（未回答またはデータなし）');
      // 「回答なし」が確定しているため、null（判定不能）ではなく空の履歴を返す
      return { user_id: userId, lecture_id: lectureId, tenant_id: tenantId, answers: [] };
    }

    if (!response.ok) {
      console.warn('[Question] 回答履歴取得エラー:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[Question] 回答履歴取得成功:', data);
    return data;
  } catch (error) {
    console.warn('[Question] 回答履歴取得に失敗:', error);
    return null;
  }
}

/**
 * ユーザーのランキング情報を CDK REST API から取得する。
 *
 * GET /user-ranking?tenant_id=xxx&user_id=xxx&lecture_id=xxx
 *
 * 講義終了後、受講生個人のランキング情報を取得する。
 */
export async function fetchUserRanking(
  tenantId: string,
  userId: string,
  lectureId: string,
): Promise<UserRankingResponse | null> {
  await ensureFrontendSettings();
  const config = getQuestionConfig();
  if (!config.restApiUrl) {
    console.warn('[Question] REST API URLが設定されていません');
    return null;
  }

  const url = `${config.restApiUrl}/user-ranking?tenant_id=${encodeURIComponent(tenantId)}&user_id=${encodeURIComponent(userId)}&lecture_id=${encodeURIComponent(lectureId)}`;

  try {
    console.log('[Question] ランキング情報を取得:', { tenantId, userId, lectureId, url });
    
    const response = await fetch(url, {
      headers: {
        'x-api-key': config.apiKey,
      },
    });

    if (response.status === 404) {
      console.log('[Question] ランキング情報が見つかりません（未回答またはデータなし）');
      return null;
    }

    if (!response.ok) {
      console.warn('[Question] ランキング情報取得エラー:', response.status);
      return null;
    }

    const data = await response.json();
    console.log('[Question] ランキング情報取得成功:', data);
    return data;
  } catch (error) {
    console.warn('[Question] ランキング情報取得に失敗:', error);
    return null;
  }
}

// =============================================================================
// 変換ユーティリティ
// =============================================================================

/**
 * バックエンドのステータスをフロントエンドのステータスに変換
 */
export function mapBackendStatus(
  backendStatus: string,
): Question['status'] {
  switch (backendStatus) {
    case 'answering':
    case 'open':
      return 'answering';
    case 'aggregating':
      return 'tallying';
    case 'aggregated':
    case 'closed':
      return 'tallied';
    case 'explanation_ended':
      return 'completed';
    case 'not_started':
    default:
      return 'not_started';
  }
}

/**
 * バックエンドの問題データをフロントエンドの Question 型に変換
 * @param forceShowExplanation 強制的に正解と解説を表示（講義終了時など）
 */
export function mapBackendQuestion(
  backendQuestion: BackendQuestion,
  questionNum: number,
  status: Question['status'],
  forceShowExplanation: boolean = false,
  displayNumber?: number | null,
): Question {
  // 回答受付中は正解情報と解説をマスクする（不正防止）
  // ただし、forceShowExplanation=trueの場合は常に表示
  const shouldMaskAnswer = !forceShowExplanation && (status === 'answering' || status === 'not_started');

  const choices: Choice[] = backendQuestion.choices.map((c, index) => ({
    id: c.num ?? (index + 1),  // c.numを最優先、なければindex+1。c.idは使用しない
    internal_id: c.internal_id ?? '',
    choice: c.choice ?? c.text ?? '',
    sort_key: c.sort_key ?? (index + 1),
    has_additional_question: c.has_additional_question ?? false,
    additional_question: c.additional_question ?? null,
    is_correct: shouldMaskAnswer ? false : (c.is_correct ?? false),
    description: shouldMaskAnswer ? '' : (c.description ?? ''),
  }));

  const images: Array<{ url: string; label?: string }> | null = backendQuestion.question_images
    ? backendQuestion.question_images.map((img) => {
        if (typeof img === 'string') {
          return { url: img };
        }
        // file_url または image_url のどちらかを取得
        const url = img.file_url || img.image_url || '';
        return url !== '' ? { url, label: img.label } : null;
      }).filter((item): item is { url: string; label?: string } => item !== null)  // nullを除外
    : null;

  return {
    id: backendQuestion.question_id ?? backendQuestion.id ?? questionNum,
    internal_id: backendQuestion.internal_id ?? null,
    question_type: (backendQuestion.question_type as Question['question_type']) ?? 'single',
    question_text: backendQuestion.question_text ?? backendQuestion.questiontext ?? '',
    question_images: images,
    level: backendQuestion.level ?? 1,
    format_type: (backendQuestion.format_type as Question['format_type']) ?? 'question',
    display_number: displayNumber ?? null,
    time_limit: backendQuestion.time_limit ?? backendQuestion.limittime ?? 60,
    help_text: backendQuestion.help_text ?? '',
    chart_style: (backendQuestion.chart_style as Question['chart_style']) ?? 0,
    status,
    sort_key: backendQuestion.sort_key ?? questionNum,
    choices,
  };
}
